import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeading } from '../components/PageHeader'
import { useBulkMediaAccess } from './access'
import { AccountPicker } from './AccountPicker'
import { bulkMediaApi, type BulkMediaApi } from './api'
import { CategoryPicker } from './CategoryPicker'
import { normName } from './format'
import { FileList } from './FileList'
import { JobsPanel } from './JobsPanel'
import { MAX_ACCOUNTS, MAX_CATEGORIES, MAX_FILES } from './stage'
import type { BulkMediaAccount, CategoryOverview } from './types'
import { useStagedFiles } from './useStagedFiles'
import './bulkMedia.css'

/** Wait this long after the Account choice settles before reading their Categories. */
const OVERVIEW_DEBOUNCE_MS = 600

export function BulkMediaPage() {
  const access = useBulkMediaAccess()
  if (access === null) return null
  if (!access) return <Navigate to="/" replace />
  return <BulkMediaView api={bulkMediaApi} />
}

/**
 * Bulk Media (CONTEXT.md; ADR 0025/0026): the same photos and videos to many
 * OnlyFans Accounts, into Categories matched by name — or only the Categories.
 * Files go to the console once; the jobs process does the OnlyFans work, so the
 * tab may close after the job starts.
 */
export function BulkMediaView({ api }: { api: BulkMediaApi }) {
  const { t } = useTranslation('bulkMedia')
  const [accounts, setAccounts] = useState<BulkMediaAccount[] | null>(null)
  const [accountsError, setAccountsError] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [overview, setOverview] = useState<CategoryOverview | null>(null)
  const [overviewFor, setOverviewFor] = useState('')
  const [overviewError, setOverviewError] = useState('')
  const [extra, setExtra] = useState<string[]>([])
  const [picked, setPicked] = useState<string[]>([])
  const files = useStagedFiles(api, { limit: MAX_FILES })
  const [refused, setRefused] = useState<{ name: string; reason: string }[]>([])
  const [submitting, setSubmitting] = useState<'upload' | 'categories' | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [created, setCreated] = useState<{ id: string; n: number } | null>(null)

  useEffect(() => {
    api.accounts().then(setAccounts, (e: unknown) => setAccountsError(e instanceof Error ? e.message : String(e)))
  }, [api])

  // Category overview for the chosen Accounts (read live from OnlyFans, so debounced).
  const key = useMemo(() => [...selected].sort().join(','), [selected])
  useEffect(() => {
    if (!key) {
      setOverview(null)
      setOverviewFor('')
      return
    }
    let stale = false
    const timer = window.setTimeout(() => {
      setOverviewError('')
      api.categories(key.split(',')).then(
        (o) => {
          if (stale) return
          setOverview(o)
          setOverviewFor(key)
        },
        (e: unknown) => {
          if (stale) return
          setOverviewError(e instanceof Error ? e.message : String(e))
          setOverviewFor(key)
        },
      )
    }, OVERVIEW_DEBOUNCE_MS)
    return () => {
      stale = true
      window.clearTimeout(timer)
    }
  }, [api, key])

  // Leaving with files still going up would lose them.
  useEffect(() => {
    if (!files.busy) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [files.busy])

  const ready = files.entries.filter((f) => f.status === 'ready')
  const failed = files.entries.filter((f) => f.status === 'failed')
  const names = picked
  const canUpload = selected.length > 0 && ready.length > 0 && !files.busy && !failed.length && !submitting
  const canCreateCategories = selected.length > 0 && names.length > 0 && !submitting

  // Categories every chosen Account already has need no creating.
  const toCreate = useMemo(() => {
    if (!overview) return names.length * selected.length
    return names.reduce((n, name) => {
      const c = overview.categories.find((x) => normName(x.name) === normName(name))
      const have = c ? c.account_ids.filter((id) => selected.includes(id)).length : 0
      return n + (selected.length - have)
    }, 0)
  }, [names, overview, selected])

  const start = async (kind: 'upload' | 'categories') => {
    setSubmitting(kind)
    setSubmitError('')
    try {
      const job = await api.createJob({
        kind,
        source: 'bulk_media',
        account_ids: selected,
        category_names: names,
        file_ids: kind === 'upload' ? ready.map((f) => f.fileId as string) : [],
      })
      if (kind === 'upload') files.reset()
      setCreated((c) => ({ id: job.id, n: (c?.n ?? 0) + 1 }))
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(null)
    }
  }

  const summary =
    selected.length === 0
      ? t('summary.pickAccounts')
      : t('summary.line', {
          files: t('summary.files', { count: ready.length }),
          accounts: t('summary.accounts', { count: selected.length }),
          categories: names.length ? t('summary.categories', { count: names.length }) : t('summary.noCategory'),
        })

  return (
    <div className="mobile-page-shell cpt-root bm-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading title={t('title')} subtitle={t('subtitle')} />
      <div className="bm-layout">
        <div className="bm-compose">
          <Section n={1} title={t('accounts.title')} aside={t('accounts.count', { count: selected.length, max: MAX_ACCOUNTS })}>
            <AccountPicker accounts={accounts} loadError={accountsError} selected={selected} onChange={setSelected} max={MAX_ACCOUNTS} />
          </Section>
          <Section n={2} title={t('categories.title')} aside={t('categories.optional')}>
            <CategoryPicker
              accountIds={selected}
              accounts={accounts ?? []}
              coverage={overview?.categories ?? null}
              loading={Boolean(key) && overviewFor !== key}
              errors={overviewFor === key ? overview?.errors ?? {} : {}}
              loadError={overviewFor === key ? overviewError : ''}
              extra={extra}
              onExtra={setExtra}
              picked={picked}
              onPick={setPicked}
              max={MAX_CATEGORIES}
            />
          </Section>
          <Section n={3} title={t('files.title')} aside={files.entries.length ? `${files.entries.length}/${MAX_FILES}` : undefined}>
            <FileList
              entries={files.entries}
              onAdd={(list) => setRefused(files.add(list))}
              onDrop={files.drop}
              onRetry={files.retry}
              max={MAX_FILES}
            />
            {refused.length > 0 && (
              <p className="bm-error">
                {refused.map((r) => t(`files.refused.${r.reason}`, { name: r.name, max: MAX_FILES })).join(' ')}
              </p>
            )}
          </Section>
          <div className="bm-submit">
            <p className="bm-summary">{summary}</p>
            {names.length > 0 && selected.length > 0 && toCreate > 0 && (
              <p className="bm-muted">{t('summary.willCreate', { count: toCreate })}</p>
            )}
            {files.busy && <p className="bm-muted">{t('summary.staging')}</p>}
            {failed.length > 0 && <p className="bm-error">{t('summary.failedFiles', { count: failed.length })}</p>}
            {submitError && <p className="bm-error">{submitError}</p>}
            <div className="bm-submit-actions">
              <button type="button" className="bm-btn" disabled={!canCreateCategories} onClick={() => void start('categories')}>
                {submitting === 'categories' ? t('starting') : t('createCategories')}
              </button>
              <button type="button" className="bm-btn bm-btn--primary" disabled={!canUpload} onClick={() => void start('upload')}>
                {submitting === 'upload' ? t('starting') : t('upload', { count: ready.length })}
              </button>
            </div>
          </div>
        </div>
        <JobsPanel api={api} created={created} />
      </div>
    </div>
  )
}

function Section({ n, title, aside, children }: { n: number; title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="bm-section" aria-label={title}>
      <header className="bm-section-head">
        <span className="bm-step" aria-hidden="true">
          {n}
        </span>
        <h2 className="bm-h2">{title}</h2>
        {aside && <span className="bm-muted bm-section-aside">{aside}</span>}
      </header>
      {children}
    </section>
  )
}
